// Compose a short, readable narrative for the user's analysis.
// Falls back gracefully when no content is available.

import { aggregateBiomarkers, deriveFlag } from "@/lib/resParser";

export function buildAnalysisSummary({ parsed, result, lang = "es" }) {
  const biomarkers = parsed?.biomarkers || [];
  if (biomarkers.length === 0) return null;
  const agg = aggregateBiomarkers(biomarkers);
  const altered = biomarkers.filter((b) => !b.isNormal);

  const topHigh = altered
    .filter((b) => deriveFlag(b) === "high")
    .slice(0, 3)
    .map((b) => b.name.toLowerCase());
  const topLow = altered
    .filter((b) => deriveFlag(b) === "low")
    .slice(0, 3)
    .map((b) => b.name.toLowerCase());

  const algoSentences = [];
  if (result?.algorithmResult) {
    Object.values(result.algorithmResult).forEach((a) => {
      if (a?.SUCCESS && a.description) {
        // First sentence
        const firstSent = a.description.split(/\.(?:\s|$)/)[0];
        if (firstSent && firstSent.length < 240) algoSentences.push(firstSent);
      }
    });
  }

  const tpl = {
    es: {
      head:
        `Tu informe contiene ${agg.total} biomarcadores. ` +
        `${agg.normal} dentro de rango (${Math.round(
          (agg.normal / agg.total) * 100
        )}%) y ${agg.altered} fuera de rango.`,
      high: topHigh.length
        ? ` Destaca por encima del rango: ${topHigh.join(", ")}.`
        : "",
      low: topLow.length ? ` Por debajo del rango: ${topLow.join(", ")}.` : "",
      join: " ",
      empty:
        "Aún no hay un resumen automático para este informe. Revisa los biomarcadores y la evaluación clínica abajo, o pregúntame a través del chat.",
    },
    en: {
      head:
        `Your report contains ${agg.total} biomarkers. ` +
        `${agg.normal} in range (${Math.round(
          (agg.normal / agg.total) * 100
        )}%) and ${agg.altered} out of range.`,
      high: topHigh.length ? ` Above range: ${topHigh.join(", ")}.` : "",
      low: topLow.length ? ` Below range: ${topLow.join(", ")}.` : "",
      join: " ",
      empty:
        "No automatic summary is available for this report yet. Review the biomarkers and clinical evaluation below, or ask through chat.",
    },
    fr: {
      head:
        `Votre rapport contient ${agg.total} biomarqueurs. ` +
        `${agg.normal} dans la norme (${Math.round(
          (agg.normal / agg.total) * 100
        )}%) et ${agg.altered} hors norme.`,
      high: topHigh.length ? ` Au-dessus : ${topHigh.join(", ")}.` : "",
      low: topLow.length ? ` En dessous : ${topLow.join(", ")}.` : "",
      join: " ",
      empty: "Pas encore de résumé automatique. Consultez les biomarqueurs et l'évaluation ci-dessous.",
    },
    ca: {
      head:
        `El teu informe conté ${agg.total} biomarcadors. ` +
        `${agg.normal} dins de rang (${Math.round(
          (agg.normal / agg.total) * 100
        )}%) i ${agg.altered} fora de rang.`,
      high: topHigh.length ? ` Per sobre del rang: ${topHigh.join(", ")}.` : "",
      low: topLow.length ? ` Per sota del rang: ${topLow.join(", ")}.` : "",
      join: " ",
      empty:
        "Encara no hi ha un resum automàtic. Revisa biomarcadors i avaluació clínica a sota.",
    },
  };
  const T = tpl[lang] || tpl.es;

  const summary = `${T.head}${T.high}${T.low}`;
  const detail = algoSentences.slice(0, 3).join(". ");
  return {
    text: detail ? `${summary} ${detail}.` : summary,
    isEmpty: false,
  };
}

export function placeholderSummary(lang = "es") {
  const m = {
    es: "Aún no podemos generar un resumen automático con los datos disponibles. Revisa los biomarcadores y la evaluación clínica más abajo, o pregúntale a Andy.",
    en: "We can't generate an automatic summary with the available data yet. Review biomarkers and clinical evaluation below, or ask Andy.",
    fr: "Impossible de générer un résumé automatique avec les données disponibles. Consultez les biomarqueurs ci-dessous ou demandez à Andy.",
    ca: "Encara no podem generar un resum automàtic. Revisa biomarcadors i l'avaluació clínica a sota, o pregunta a l'Andy.",
  };
  return m[lang] || m.es;
}
